/* eslint-disable no-lonely-if */
/**
 * Session Viewer (route: /app/store-explorer-v2).
 *
 * Session data is assembled from three endpoints fetched in parallel -
 * get_store_photos/images, /misc_data and /variants - which together replace
 * the retired monolithic /api/v1/get_store_photos/. The fan-out itself lives in
 * SidebarMenuNew, which owns every session-data dispatch; genericSEReducer
 * merges the three responses into a single `sessionData` object of the shape
 * ShelfVisualizer expects, and guarantees `images`/`variants` are always arrays
 * and `store_explorer_settings`/`color_legend` always objects - the visualiser
 * throws on undefined but tolerates empty.
 *
 * Any one leg failing fails the page, reported with copy that names the leg but
 * never echoes the server's error text (it can carry the DB schema and the
 * client's internal name).
 *
 * With REACT_APP_SHELF_PROGRESSIVE_RENDER=true the page mounts as soon as
 * images and misc_data land, filling the SKU table in when variants arrives.
 * The flag is absent from every committed env file, so the default is to wait
 * for all three.
 */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { array, bool, func, object, string } from 'prop-types';
import { connect } from 'react-redux';
import { each, isEmpty, map } from 'lodash';
import * as noDataAnimation from 'assets/animations/no_data.json';
import Lottie from 'react-lottie';

// redux-utils
import {
  appActions,
  genericSEActions,
  chartActions
} from 'redux-utils/actions/index';
import {
  appSelector,
  genericSESelector,
  slicerSelector
} from 'redux-utils/selectors/index';
import { isProgressiveRenderEnabled } from 'redux-utils/selectors/genericSESelector';
import { useHistory, useLocation } from 'react-router-dom';
// lib
import { getFilterValue } from 'utils/filters';
import Stack from 'lib/core/stack';
// components
import Loader from 'components/Loader/Loader';
// views
import { getRouteID } from 'lib/utils/getActiveRoute';
import NoDataMessage from './NoDataMessage';
import SessionDataError from './SessionDataError';
import GenericSEFilters from './GenericSEFilters';
import ShelfVisualizer from '@infilect_developer/shelf-visualiser';
import ChartRenderer from 'pages/Common/ChartRenderer';
import Icon from 'components/Icon/Icon';
import { DownloadIcon } from 'assets/icons/appIcons';
import Notification from 'components/Notification/Notification';
import ls from 'lib/core/storageFactory';

import downloadStackedImage from './stackedImage';
import { groupByBrandCategorySubcategory } from 'utils/groupByBrandCategorySubcategory';
import { unstable_batchedUpdates } from 'react-dom';
import { priorityColorJSON } from 'utils/priorityColor';
import useReasonCodes, {
  useSessionReviewStatus
} from 'pages/TestStoreExplorer/useReasonCodes';

export const brandBlockingTabKey = 'ch-brand-blocking';
export const skuBlockingTabKey = 'ch-sku-blocking';

const SESSION_VIEWER_PATH = '/app/store-explorer-v2';

const computeFilterData = (filtersConfiguredFromAPI, filterData) => {
  const filtersToRender = new Stack();
  let previousFilterValue = null;
  filtersConfiguredFromAPI.forEach(({ label, value }) => {
    let options = filterData[value];
    if (!filtersToRender.isEmpty()) {
      const parentFilter = filtersToRender.peek();
      const parentValue = (parentFilter.value || {}).value;
      if (!previousFilterValue) {
        previousFilterValue = `${parentValue}`;
      } else {
        previousFilterValue += `+${parentValue}`;
      }
      options = (options || []).reduce((options, option) => {
        if (option.previous_filter_value === previousFilterValue) {
          options.push(option);
        }
        return options;
      }, []);
    }
    const obj = { label, key: value, options };
    obj.value = getFilterValue({ options: obj.options });
    filtersToRender.push(obj);
  });
  return filtersToRender;
};

let filterStack = [];

const noDataAnimationOptions = {
  loop: true,
  autoplay: true,
  animationData: noDataAnimation.default
};

const GenericSE = ({
  areFiltersSet,
  chartData,
  chartList,
  dispatchChartDataRequest,
  dispatchChartListRequest,
  filterData,
  layout,
  routeID,
  sessionData,
  dispatchDownloadChartDataRequest,
  reportData,
  layoutCSS,
  dashboardType,
  routes,
  dispatchGetLayoutRequest,
  sessionFilters,
  dispatchSessionFiltersValuesRequest,
  chartParamsValue,
  dispatchFilterChange,
  filterPrefs,
  sessionFilterValues,
  sessionFilterValuesStatus,
  clearSplitSessionData,
  sidebarFilters,
  bottomUpFilters,
  clientPreferences,
  isFetchingSplitSessionData,
  splitSessionDataFailedLeg,
  isSplitSessionDataReady,
  isSplitSessionDataComplete
}) => {
  const { pathname, search } = useLocation();
  const [isFiltersReadyToRender, setFiltersRenderState] = useState(false);
  const [tableData, setTableData] = useState(null);
  const [filtersToRender, setFiltersToRender] = useState({ data: [] });
  const [hasDownloadedFile, sethasDownloadedFile] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [staticChartData, setStaticChartData] = useState({});
  const [staticCartList, setStaticCartList] = useState([]);
  const filtersConfiguredFromAPI = layout?.defaults;
  const [notification, setNotification] = useState({
    display: false,
    message: '',
    position: ''
  });

  const { reasonCodes } = useReasonCodes();

  const { sessionReviewStatus } = useSessionReviewStatus(
    sessionData,
    reasonCodes
  );

  const handleNotificationCloseAndRefresh = (_event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ display: false, message: '', position: '' });
  };
  const handleNotificationClose = (_event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ display: false, message: '', position: '' });
  };

  const routeIDParam = getRouteID(window.location.pathname);

  const getFilterParamsForAPI = (filters = filterStack.data) => {
    const params = {};
    filters.forEach(f => {
      params[f.key] = (f.value || {}).value;
    });
    return params;
  };

  useEffect(() => {
    const session = new URLSearchParams(search).get('q');
    setSessionId(session);
  }, [search]);

  useEffect(() => {
    clearSplitSessionData();
  }, []);

  useEffect(() => {
    if (sessionId) {
      dispatchSessionFiltersValuesRequest({
        params: { q: sessionId, route_id: layout?.routeID }
      });
    }
  }, [sessionId]);

  useEffect(() => {
    if (Object.keys(sessionFilterValues).length && sessionId) {
      const newState = { ...filtersToRender.data };
      Object.keys(newState).reduce((accu, item) => {
        const filterKey = newState[item].key;
        newState[item].value = sessionFilterValues[filterKey];
      }, {});
      let obj = { ...sessionFilterValues };
      if (filterStack.data) {
        Object.keys(obj).map(p => {
          const sliceIndex = filtersConfiguredFromAPI.findIndex(
            el => el.value === p
          );

          const filterValues = getFilterParamsForAPI();
          const retainedFilters = filtersConfiguredFromAPI.slice(0, sliceIndex);

          let previousFilterValue = null;
          retainedFilters.forEach(filterValue => {
            if (!previousFilterValue) {
              previousFilterValue = `${filterValues[filterValue.value]}`;
            } else {
              previousFilterValue += `+${filterValues[filterValue.value]}`;
            }
          });

          let options = filterData[p];

          options = (options || []).reduce((options, option) => {
            if (option.previous_filter_value === previousFilterValue) {
              options.push(option);
            }
            return options;
          }, []);

          obj[p] = Object.assign(
            { previous_filter_value: previousFilterValue, options },
            obj[p]
          );
        });

        filterStack.data.map(filter => {
          filter.value = obj[filter.key];
          filter.options = obj[filter.key].options;
        });
        setFiltersToRender({ data: filterStack.data, key: Math.random() });
        setFiltersRenderState(true);
      }
    }
  }, [sessionFilterValues, filterStack.data]);

  useEffect(() => {
    if (
      filterStack?.data?.length &&
      !Object.keys(filterPrefs)?.length &&
      chartList.length
    ) {
      let filterList = {};
      filterStack.data.map(filter => {
        filterList[filter.key] = filter.value;
      });
      dispatchFilterChange({ routeID, value: filterList, chartList });
    }
  }, [filterStack.data, chartList]);

  const prepareChartListAPICall = () => {
    if (!filterStack.data.some(filter => !filter.value)) {
      dispatchChartListRequest({
        params: {
          route_id: routeID,
          ...getFilterParamsForAPI(filterStack.data)
        }
      });
    }
  };

  const prepareChartDataAPICall = ({ chart }) => {
    if (!filterStack.data.some(filter => !filter.value)) {
      dispatchChartDataRequest({
        params: {
          chart_name: chart.name,
          fl_chart_type: chart.type,
          ...getFilterParamsForAPI(filterStack.data)
        }
      });
    }
  };

  useEffect(() => {
    if (!isEmpty(filtersConfiguredFromAPI) && !isEmpty(filterData)) {
      filterStack = computeFilterData(filtersConfiguredFromAPI, filterData);
      setFiltersToRender({ data: filterStack.data, key: Math.random() });
      setFiltersRenderState(true);
      prepareChartListAPICall();
    }
  }, [filtersConfiguredFromAPI, filterData]);

  // re-runs whenever the app route changes
  useEffect(() => {
    dispatchGetLayoutRequest();
  }, [routeIDParam]);

  useEffect(() => {
    if (
      chartList.length > 0 &&
      dashboardType !== 'v2' &&
      !isEmpty(filterData)
    ) {
      chartList.forEach(chart => {
        prepareChartDataAPICall({ chart });
      });
    }
  }, [chartList]);

  const handleFilterChange = p => {
    while (filterStack.peek().key !== p.key) {
      filterStack.pop();
    }
    const sliceIndex = filtersConfiguredFromAPI.findIndex(
      el => el.value === p.key
    );
    const slicedFiltersFromAPI = filtersConfiguredFromAPI.slice(
      sliceIndex,
      filtersConfiguredFromAPI.length
    );

    const filterValues = getFilterParamsForAPI();
    const retainedFilters = filtersConfiguredFromAPI.slice(0, sliceIndex);
    let previousFilterValue = null;
    retainedFilters.forEach(filter => {
      if (!previousFilterValue) {
        previousFilterValue = `${filterValues[filter.value]}`;
      } else {
        previousFilterValue += `+${filterValues[filter.value]}`;
      }
    });
    slicedFiltersFromAPI.forEach(({ label, value }, index) => {
      if (index > 0) {
        const parentFilter = filterStack.peek();
        const parentValue = (parentFilter.value || {}).value;
        if (!previousFilterValue) {
          previousFilterValue = `${parentValue}`;
        } else {
          previousFilterValue += `+${parentValue}`;
        }
        const obj = {
          label,
          key: value,
          options: filterData[value]?.reduce((options, option) => {
            if (option.previous_filter_value === previousFilterValue) {
              options.push(option);
            }
            return options;
          }, [])
        };
        obj.value = getFilterValue({ options: obj.options });
        filterStack.push(obj);
      } else {
        const changedFilter = filterStack.pop();
        const obj = { ...changedFilter };
        obj.value = p.val;
        filterStack.push(obj);
      }
    });
    setFiltersToRender({ data: filterStack.data, key: Math.random() });
    prepareChartListAPICall();
    let filterList = {};
    filterStack.data.map(filter => {
      filterList[filter.key] = filter.value;
    });
    dispatchFilterChange({ routeID, value: filterList, chartList });
  };

  useEffect(() => {
    if (sessionData.images?.length > 0 && sessionData.variants?.length > 0) {
      computeTableData(sessionData.variants);
    }
  }, [sessionData, computeTableData]);

  const computeTableData = useCallback(
    data => {
      const columns = [];
      const ignoreKeys = ['color', 'min', 'max', 'delta'];
      const tableObject = { datasets: [], columns: [] };
      data[0].details.forEach(item => {
        map(item, (_v, key) => {
          if (!ignoreKeys.includes(key)) {
            columns.push(key);
            tableObject.columns.push({
              label: key.split('_').join(' '),
              dataKey: key
            });
          }
        });
      });
      data.forEach(el => {
        const { bounding_boxes: bnBox, details } = el;
        const tempItem = { bnBox };
        details.forEach(item => {
          const itemKeys = Object.keys(item);
          const arrays = [itemKeys, columns];
          const result = arrays
            .sort((a, b) => {
              return a.length - b.length;
            })
            .shift()
            .filter(v => {
              return arrays.every(a => {
                return a.indexOf(v) !== -1;
              });
            });
          if (itemKeys.includes('color')) {
            // eslint-disable-next-line prefer-destructuring
            tempItem[result[0]] = (
              <p style={{ color: item.color, margin: 0 }}>{item[result[0]]}</p>
            );
          } else if (itemKeys.includes('price_compliance')) {
            tempItem[result[0]] = item;
          } else {
            if (result[0] === 'count') {
              if (item.count !== 0) {
                tempItem[result[0]] = item[result[0]];
              }
            } else {
              tempItem[result[0]] = item[result[0]];
            }
          }
        });
        tableObject.datasets.push({ ...tempItem, hide: !tempItem.count });
      });
      // concat other brand to last of array
      const position = tableObject.datasets.map(e => e.brand).indexOf('Others');
      tableObject.datasets = tableObject.datasets.concat(
        tableObject.datasets.splice(position, 1)
      );
      setTableData(tableObject);
    },
    [sessionData]
  );

  // onProgress comes from the button, which uses it to swap its label from
  // "combining" to the real transferred byte count. Returning the message
  // rather than throwing is what the button expects: truthy means it should
  // stop reporting progress, and this shows the toast.
  const handleStackedImageDownload = async onProgress => {
    // sessionData.images is the exact array the shelf and the minimap render,
    // so its order IS the on-screen order. Sending the ids is what guarantees
    // the download contains the same images the user is looking at: the
    // session-wide query on the server is a superset, because the images leg
    // additionally filters by placement, category, review state and whether a
    // photo has any facings.
    const errorMessage = await downloadStackedImage({
      photoIds: (sessionData.images || []).map(image => image.id),
      sessionId: sessionData.session_data?.[0] || sessionId,
      gap: sessionData?.store_explorer_settings?.stacked_image_gap || 0,
      onProgress
    });

    if (errorMessage) {
      setNotification({
        display: true,
        message: errorMessage,
        position: 'top',
        callback: handleNotificationClose
      });
    }

    return errorMessage;
  };

  const handleDataDownload = () => {
    dispatchDownloadChartDataRequest({
      params:
        dashboardType !== 'v2'
          ? {
              fl_chart_type: 'table',
              report_format: 'excel',
              ...getFilterParamsForAPI(filterStack.data)
            }
          : {
              fl_chart_type: 'table',
              report_format: 'excel',
              ...sessionFilters
            }
    });
    sethasDownloadedFile(false);
  };

  // NOTE: `slicers = slicers` is a self-referential default that throws
  // ReferenceError (TDZ) whenever this is called with two args - which
  // handleChartDownload always does. Cloned verbatim from GenericSE so v3 stays
  // behaviourally identical to v2; it is a pre-existing v2 bug, not a v3 one,
  // and fixing it here would make the two pages diverge during QA.
  const computeRequestParams = (chart, chartParamsValue, slicers = slicers) => {
    let requestObject = {};
    each(chart.params, param => {
      const { param_id: paramID } = param;
      if (paramID.includes('gr_')) {
        if (chartParamsValue[chart.name]) {
          if (chartParamsValue[chart.name][paramID]) {
            requestObject[paramID] =
              chartParamsValue[chart.name][paramID].value;
          }
        }
      } else if (
        paramID === 'fl_start_date' ||
        paramID === 'fl_end_date' ||
        paramID === 'fl_time_type'
      ) {
        requestObject.fl_end_date = chartParamsValue?.fl_time?.fl_end_date;
        requestObject.fl_start_date = chartParamsValue?.fl_time?.fl_start_date;
        requestObject.fl_time_type = chartParamsValue?.fl_time?.fl_time_type;
      } else if (paramID.includes('chart_name')) {
        requestObject[paramID] = chart.name;
      } else if (!isEmpty(slicers) && slicers[chart.name][paramID]) {
        requestObject[paramID] = slicers[chart.name][paramID].value.value;
      } else {
        requestObject[paramID] =
          chartParamsValue[paramID] === undefined
            ? ''
            : chartParamsValue[paramID].value;
      }
      if (param.depend_on) {
        requestObject[paramID] =
          chartParamsValue[chart.name][paramID]?.value?.value;
      }
    });
    return requestObject;
  };

  const handleChartDownload = ({ chart }) => {
    const requestObject = computeRequestParams(chart, chartParamsValue);
    requestObject.report_format = 'excel';
    dispatchDownloadChartDataRequest({
      params: requestObject,
      cacheRequest: false,
      routeID: routeID
    });
    sethasDownloadedFile(false);
  };

  if (!hasDownloadedFile && !isEmpty(reportData)) {
    let url = '';
    if (reportData?.report_file) {
      url = reportData.report_file;
    } else {
      if (reportData) {
        const blob = new Blob([reportData], { type: 'text/csv' });
        url = window.URL.createObjectURL(blob);
      }
    }

    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = 'download.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      sethasDownloadedFile(true);
    }
  }

  useEffect(() => {
    if (sessionFilterValuesStatus.error) {
      setNotification({
        display: true,
        message: sessionFilterValuesStatus.message,
        callback: handleNotificationCloseAndRefresh
      });
    }
  }, [sessionFilterValuesStatus]);

  const doesLatestFilterHaveValue = !!bottomUpFilters[
    bottomUpFilters.incomingFilter
  ]?.options.length;

  const [sessionUrl, setSessionUrl] = useState('');

  useEffect(() => {
    if (sessionData.session_data?.[0]) {
      const currentSessionId = sessionData.session_data[0];
      // stays on v3 so a copied link keeps QA on the split-API page
      const url = `https://${ls.get('domain').toLowerCase()}.${
        process.env.REACT_APP_SHELF_REDIRECT_URI
      }${SESSION_VIEWER_PATH}?q=${currentSessionId}`;
      window.FreshworksWidget('prefill', 'ticketForm', {
        custom_fields: { cf_session_id: currentSessionId }
      });
      setSessionUrl(url);
      window.history.replaceState(
        null,
        null,
        `${pathname}?q=${currentSessionId}`
      );
    }
  }, [sessionData, tableData]);

  useEffect(() => {
    if (sessionData.variants && clientPreferences?.se_web?.brand_blocking) {
      const brandBlockingData = groupByBrandCategorySubcategory(
        sessionData.variants
      );

      if (brandBlockingData.length > 0) {
        unstable_batchedUpdates(() => {
          setStaticCartList(prev => {
            const tempPrev = prev.filter(
              res => res.name !== brandBlockingTabKey
            );
            return [
              ...tempPrev,
              {
                params: [],
                type: 'table',
                name: brandBlockingTabKey,
                title: 'Brand Blocking',
                granularities: [],
                filters: [],
                slicers: [],
                excel_report: false
              }
            ];
          });
          setStaticChartData(prev => {
            return {
              ...prev,
              [brandBlockingTabKey]: {
                height: 520,
                title: 'Sku Secondary Display',
                datasets: brandBlockingData,
                columns: [
                  { label: 'BRAND', dataKey: 'brand' },
                  { label: 'CATEGORY', dataKey: 'category' },
                  { label: 'SUB CATEGORY', dataKey: 'subcategory' },
                  { label: 'COUNT', dataKey: 'count' },
                  { label: 'COLOR-COL', dataKey: 'color' }
                ]
              }
            };
          });
        });
      }
    }
  }, [sessionData, clientPreferences]);

  const getColorBasedOnBrandPriority = (detectionID, tabType) => {
    if (tabType === brandBlockingTabKey || tabType === skuBlockingTabKey) {
      let index = '20';
      const datasets = staticChartData[tabType]?.datasets || [];
      let tempIndex = datasets.findIndex(res =>
        res.detections.includes(detectionID)
      );

      if (tempIndex >= 0) {
        index = tempIndex;
      }

      if (index > 20) {
        index = 20;
      }
      index = index.toString();

      return priorityColorJSON[index];
    }
    return null;
  };

  // A leg failed. Any leg failing fails the page, and the reducer has already
  // dropped the partial assembly, so this takes precedence over every other
  // state - including the "no data" message, which would otherwise misreport a
  // server error as an empty session (the v2 behaviour being fixed here).
  const renderBody = () => {
    if (splitSessionDataFailedLeg) {
      return <SessionDataError failedLeg={splitSessionDataFailedLeg} />;
    }

    // Blocking mode reproduces v2 exactly: loader while the fetch is in flight
    // or the filter cascade is still resolving. Progressive mode releases it
    // early, as soon as images+misc are in, with variants still on the wire.
    const isBlockedOnFetch = isProgressiveRenderEnabled
      ? !isSplitSessionDataReady &&
        (isFetchingSplitSessionData || !areFiltersSet)
      : isFetchingSplitSessionData || !areFiltersSet;

    if (isBlockedOnFetch) {
      return <Loader plain circular centered color="secondary" inline />;
    }

    if (!doesLatestFilterHaveValue) {
      return (
        <div>
          <NoDataMessage
            shared={true}
            message={
              'Session unavailable. Try adjusting your filters or contact an admin for access to this session'
            }
          />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Lottie
              options={noDataAnimationOptions}
              height={'12em'}
              width={'12em'}
              isStopped={false}
              style={{}}
            />
          </div>
        </div>
      );
    }

    if (!isSplitSessionDataReady || !sessionData?.images?.length) {
      return (
        <NoDataMessage
          otherMessage={
            isSplitSessionDataComplete &&
            !sessionData.images?.length &&
            !sessionData.variants?.length
              ? 'Please navigate to App Images'
              : ''
          }
        />
      );
    }

    return (
      <>
        {/* progressive mode only: the visualiser's own empty-table state reads
            as "SKU table is not enabled", so say what is actually happening. */}
        {!isSplitSessionDataComplete && (
          <div
            style={{
              padding: '6px 10px',
              fontSize: '13px',
              opacity: 0.8,
              color: dashboardType === 'v2' ? '#424242' : '#ffffff99'
            }}
          >
            Loading SKU data…
          </div>
        )}
        <ShelfVisualizer
          enablePlanogramCompliance={
            sessionData?.store_explorer_settings?.enable_planogram_compliance
          }
          enablePSADownload={
            sessionData?.store_explorer_settings?.enable_psa_download
          }
          enableStackedImageDownload={Boolean(
            sessionData?.store_explorer_settings?.enable_stacked_image_download
          )}
          onDownloadStackedImage={handleStackedImageDownload}
          defaultCheckboxSelection={
            sessionData?.store_explorer_settings?.default_selection
          }
          imageDebounceTime={
            sessionData?.store_explorer_settings?.image_debounce_time
          }
          PSAFile={sessionData.psa_file || ''}
          sessionData={sessionData}
          sessionName={
            bottomUpFilters['fl_store']?.hashedOptions[
              sidebarFilters['fl_store']
            ]?.label
          }
          sessionDate={
            bottomUpFilters['fl_dropdown_date']?.hashedOptions[
              sidebarFilters['fl_dropdown_date']
            ]?.label
          }
          fontFamily={
            dashboardType === 'v2'
              ? 'loginPageFont'
              : `'Helvetica', 'Arial', sans-serif`
          }
          theme={dashboardType === 'v2' ? 'light' : 'dark'}
          handleDataDownload={handleDataDownload}
          isTse={false}
          enableTab={true}
          shelfConfig={[
            ...[
              { key: 'zoom_button', enable: true },
              { key: 'reset_button', enable: true },
              { key: 'tabs', enable: true },
              { key: 'minimap', enable: true },
              { key: 'sku_table', enable: true },
              { key: 'shelf_canvas', enable: true },
              { key: 'zoom_button', enable: true },
              { key: 'image_caption', enable: true },
              { key: 'dynamic_checkboxes', enable: true },
              { key: 'color_legend', enable: true }
            ],
            sessionData.excel_report
              ? { key: 'sku_table_header', enable: true }
              : { key: 'sku_table_header', enable: false }
          ]}
          tabsDetails={[
            { title: 'SKU Table', default: true, key: 'sku_table' },

            ...(chartList.length > 0
              ? chartList.map((chart, index) => {
                  return {
                    title: chart.title,
                    default: false,
                    key: chart.name,
                    data: chartData[chart.name],
                    content: (
                      <ChartRenderer
                        chartList={[chartList[index]]}
                        chartDataList={{
                          [chart.name]: chartData[chart.name]
                        }}
                        cardStyles={layoutCSS?.theme?.card}
                        dashboardPanelStyles={layoutCSS?.theme?.dashboardPanel}
                        dashboardType={dashboardType}
                        handleDataDownload={handleChartDownload}
                      />
                    )
                  };
                })
              : []),
            ...(staticCartList.length > 0
              ? staticCartList.map(chart => {
                  return {
                    title: chart.title,
                    default: false,
                    key: chart.name,
                    data: staticChartData[chart.name]
                  };
                })
              : [])
          ]}
          tableHeaderContent={
            sessionData.excel_report ? (
              <Icon color="light" id="sku-excel-download-button">
                <DownloadIcon onClick={handleDataDownload} />
              </Icon>
            ) : null
          }
          sessionUrl={sessionUrl}
          handleNotification={() => {
            setNotification({
              display: ls.get('domain') ? true : false,
              message: 'Copied link to the clipboard.',
              position: 'top',
              callback: handleNotificationClose
            });
          }}
          isDisplay={
            sessionFilters?.['fl_placement']?.toLowerCase() === 'display'
          }
          priorityColorJSON={priorityColorJSON}
          getColorBasedOnBrandPriority={getColorBasedOnBrandPriority}
          sessionReviewStatus={sessionReviewStatus}
        />
      </>
    );
  };

  return (
    <div>
      {notification.display && (
        <Notification
          duration={3000}
          message={notification.message}
          isOpen={notification.display}
          handleClose={notification.callback}
          anchorOrigin={
            notification.position === 'top'
              ? { vertical: 'top', horizontal: 'right' }
              : { vertical: 'bottom', horizontal: 'right' }
          }
        />
      )}
      {dashboardType !== 'v2' && isFiltersReadyToRender && (
        <GenericSEFilters
          key={filtersToRender.key}
          handleFilterChange={handleFilterChange}
          filtersToRender={filtersToRender.data || []}
        />
      )}
      {renderBody()}
    </div>
  );
};

GenericSE.propTypes = {
  chartData: object.isRequired,
  chartList: array.isRequired,
  dispatchChartDataRequest: func.isRequired,
  dispatchChartListRequest: func.isRequired,
  filterData: object.isRequired,
  layout: object.isRequired,
  routeID: string.isRequired,
  sessionData: object.isRequired,
  dispatchDownloadChartDataRequest: func.isRequired,
  reportData: object.isRequired,
  dashboardType: string.isRequired,
  layoutCSS: object,
  routes: array.isRequired,
  areFiltersSet: bool,
  isFetchingSplitSessionData: bool,
  splitSessionDataFailedLeg: string,
  isSplitSessionDataReady: bool,
  isSplitSessionDataComplete: bool
};

GenericSE.defaultProps = {
  layoutCSS: {},
  areFiltersSet: false,
  isFetchingSplitSessionData: false,
  splitSessionDataFailedLeg: null,
  isSplitSessionDataReady: false,
  isSplitSessionDataComplete: false
};

const makeMapStateToProps = () => {
  const {
    location: { pathname }
  } = window;
  const routeID = getRouteID(pathname);
  const getParamsValueList = genericSESelector.makeGetParamsValueList();
  const getSlicers = slicerSelector.selectSlicers();

  const mapStateToProps = state => ({
    chartData: genericSESelector.selectChartData(state),
    chartList: genericSESelector.selectChartList(state),
    layout: genericSESelector.selectLayout(state),
    filterData: genericSESelector.selectFilters(state),
    routeID: genericSESelector.getRouteID(),
    sessionData: genericSESelector.selectSessionData(state),
    reportData: genericSESelector.selectReportData(state),
    sessionFilters: genericSESelector.selectSessionFilters(state),
    layoutCSS: appSelector.getLayoutCSS(state),
    dashboardType: state.layoutStore.routes?.dashboardConfig?.version,
    routes: state.layoutStore.routes?.layout,
    chartParamsValue: getParamsValueList(state, { routeID }),
    slicers: getSlicers(state, { routeID }),
    filterPrefs: state.genericSE.preferences.filters,
    sessionFilterValues: genericSESelector.selectFilterValues(state),
    sessionFilterValuesStatus: genericSESelector.sessionFilterValuesStatus(
      state
    ),
    areFiltersSet: appSelector.getAreFiltersSet(state),
    sidebarFilters: appSelector.getSidebaFilters(state),
    bottomUpFilters: genericSESelector.getBottomUpFilter(state),
    clientPreferences: appSelector.getClientPreferences(state),
    isFetchingSplitSessionData: genericSESelector.isFetchingSplitSessionData(
      state
    ),
    splitSessionDataFailedLeg: genericSESelector.selectSplitSessionDataFailedLeg(
      state
    ),
    isSplitSessionDataReady: genericSESelector.isSplitSessionDataReady(state),
    isSplitSessionDataComplete: genericSESelector.isSplitSessionDataComplete(
      state
    )
  });
  return mapStateToProps;
};

const mapDispatchToProps = {
  dispatchChartDataRequest: genericSEActions.getChartData,
  dispatchChartListRequest: genericSEActions.getChartList,
  dispatchDownloadChartDataRequest: genericSEActions.downloadChartData,
  dispatchGetLayoutRequest: () => appActions.layoutRequestAction({}),
  dispatchDownloadChartData: chartActions.downloadChartDataAction,
  dispatchFilterChange: genericSEActions.filterChangeAction,
  dispatchSessionFiltersValuesRequest: genericSEActions.getSessionFilterValues,
  clearSplitSessionData: genericSEActions.clearSplitSessionData
};

export default connect(makeMapStateToProps, mapDispatchToProps)(GenericSE);
